import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Chip,
  Button,
  Paper,
  Divider
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  Palette as ThemeIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Screenshot from './Screenshot';
import surveyeditor from '../../assets/images/surveyeditor.webp';

const SurveyBuilderBasics = () => {
  const builderFeatures = [
    {
      icon: <DragIcon color="primary" />,
      title: 'Drag & Drop Interface',
      description: 'Easily reorder questions by dragging them up or down in your survey.'
    },
    {
      icon: <AddIcon color="primary" />,
      title: 'Add Questions',
      description: 'Click the "Add Question" button to insert new questions anywhere in your survey.'
    },
    {
      icon: <EditIcon color="primary" />,
      title: 'Edit in Place',
      description: 'Click on any question or text to edit it directly in the builder.'
    },
    {
      icon: <PreviewIcon color="primary" />,
      title: 'Live Preview',
      description: 'See exactly how your survey will look to respondents as you build it.'
    }
  ];

  const questionTypes = [
    { name: 'Multiple Choice', description: 'Single or multiple selection from a list of options' },
    { name: 'Text Input', description: 'Short text, long text, or paragraph responses' },
    { name: 'Rating Scale', description: 'Likert scales, star ratings, or numeric scales' },
    { name: 'Yes/No', description: 'Simple binary choice questions' },
    { name: 'Dropdown', description: 'Select from a dropdown list of options' },
    { name: 'Date/Time', description: 'Date picker or time selection fields' },
    { name: 'File Upload', description: 'Allow respondents to upload files or images' },
    { name: 'Matrix/Grid', description: 'Multiple questions with the same answer choices' }
  ];

  const bestPractices = [
    'Keep questions clear and concise',
    'Use logical question ordering',
    'Avoid leading or biased questions',
    'Test your survey before publishing',
    'Use conditional logic to reduce survey length',
    'Make required questions obvious',
    'Provide clear instructions when needed'
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700,  }}>
        Survey Builder Basics
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Learn how to use SurveySlack's intuitive survey builder to create professional surveys 
        with our drag-and-drop interface.
      </Typography>

      {/* Getting Started */}
      <Box sx={{ mb: 6 }}>
     
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            The survey builder opens automatically when you create a new survey or click "Edit" on an existing one.
          </Typography>
        </Alert>
        
        <Grid container spacing={3}>
          {builderFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Typography variant="h6" sx={{ ml: 1, fontWeight: 500 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Builder Screenshot */}
      <Box sx={{ mb: 6 }}>
        <Box
          component="img"
          src={surveyeditor}
          alt="Main Dashboard view with callouts for key sections"
          sx={{
            mt: 3,
            width: '100%',
            borderRadius: 1,
            display: 'block',
              boxShadow: (theme) => theme.shadows[3],
          }}
        />
      </Box>

      {/* Builder Interface Overview */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Builder Interface Overview
        </Typography>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, border: '2px dashed', borderColor: 'primary.light', borderRadius: 2 }}>
                  <SettingsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Left Panel</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Question types, survey settings, and design options
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, border: '2px dashed', borderColor: 'success.light', borderRadius: 2 }}>
                  <EditIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Center Panel</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Main editing area where you build your survey
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, border: '2px dashed', borderColor: 'warning.light', borderRadius: 2 }}>
                  <PreviewIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>Right Panel</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Live preview and question properties
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Question Types */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Available Question Types
        </Typography>
        <Typography variant="body1" paragraph color="text.secondary">
          SurveySlack supports various question types to capture different kinds of data:
        </Typography>
        <Grid container spacing={2}>
          {questionTypes.map((type, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                  {type.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {type.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" href="/app/documentations/creating-surveys/question-types">
            Learn More About Question Types
          </Button>
        </Box>
      </Box>

      {/* Common Actions */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Common Builder Actions
        </Typography>
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <ListItem>
            <ListItemIcon><AddIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Adding Questions" 
              secondary="Click 'Add Question' button or use the '+' icon between existing questions"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><DragIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Reordering Questions" 
              secondary="Drag questions by their handle to reorder them in your survey"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><EditIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Editing Questions" 
              secondary="Click on any question text to edit it inline, or use the properties panel"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
            <ListItemText 
              primary="Deleting Questions" 
              secondary="Click the delete icon on any question to remove it from your survey"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><SaveIcon color="success" /></ListItemIcon>
            <ListItemText 
              primary="Saving Changes" 
              secondary="Your survey is automatically saved as you make changes"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><ThemeIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Customizing Design" 
              secondary="Use the design panel to change colors, fonts, and overall appearance"
            />
          </ListItem>
        </List>
      </Box>

      {/* Best Practices */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Survey Design Best Practices
        </Typography>
        <Card sx={{ background: 'linear-gradient(135deg, #f6f9fc 0%, #f1f4f8 100%)' }}>
          <CardContent sx={{ p: 4 }}>
            <List>
              {bestPractices.map((practice, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon>
                    <CheckIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={practice}
                    primaryTypographyProps={{ fontSize: '1rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Tips and Tricks */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
          Tips & Tricks
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Alert severity="info" sx={{ height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Keyboard Shortcuts
              </Typography>
              <Typography variant="body2">
                • Ctrl/Cmd + S: Save survey<br/>
                • Ctrl/Cmd + P: Preview survey<br/>
                • Delete: Remove selected question<br/>
                • Tab: Navigate between fields
              </Typography>
            </Alert>
          </Grid>
          <Grid item xs={12} md={6}>
            <Alert severity="success" sx={{ height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Pro Tip
              </Typography>
              <Typography variant="body2">
                Use the preview mode frequently to see how your survey looks to respondents. 
                This helps catch issues early and ensures a smooth user experience.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Box>

      {/* Next Steps */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
          Ready to dive deeper?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Explore advanced features to make your surveys even more powerful.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" href="/app/documentations/creating-surveys/question-types">
            Question Types Guide
          </Button>
          <Button variant="outlined" href="/app/documentations/creating-surveys/logic-branching">
            Logic & Branching
          </Button>
        </Box>
      </Box>

      {/* Bottom navigation CTA (Previous / Next) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Button  color="inherit" variant="contained" href="/app/documentations/building-surveys/creating-survey">
          Previous
        </Button>
         <Button
                        variant="contained"
                        size="medium"
                        href="/app/documentations/building-surveys/question-types"
                      >
                        Next
                      </Button>
      </Box>
    </Box>
  );
};

export default SurveyBuilderBasics;